<template>
    <div id="RecommendedItems" :class="{ 'pt-10': noSeparator }">
        <CLineBreak v-if="!noSeparator" class="mb-10" />

        <CHeading variant="2">
            <slot v-if="$slots.default" />
            <template v-else>
                {{ $t('relatedItems.default.headline', lang || 'sk') }}
            </template>
        </CHeading>

        <div ref="lazyComponentContainer">
            <LazyRelatedItemsBlock
                :lang="lang"
                :loading="loadingRelatedItems"
                :more-items-card="moreItemsCard"
                :no-separator="noSeparator"
                :related-items="relatedItems"
                :compact="compact"
                @on-show-more-button-click="onButtonClick"
                @item-click="emit('itemClick', $event)"
            >
                <slot />
            </LazyRelatedItemsBlock>
        </div>
    </div>
</template>

<script setup lang="ts">
import { EntityType } from '#shared/types/EntityType'
import { type RelatedItems } from '#shared/types/RelatedItems'
import { useTopMentoringAccess } from '@/composables/mentoring/useTopMentoringAccess'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { type PropType, defineAsyncComponent } from 'vue'
import type { LocationAsRelativeRaw } from 'vue-router'

const { getRelatedItems } = useTypesense()
const { topExclusionFilter } = useTopMentoringAccess()
const LazyRelatedItemsBlock = defineAsyncComponent(() => import('@/components/common/RelatedItemsBlock.vue'))

export interface ItemsCardType {
    cardLabel: string
    redirectTo: LocationAsRelativeRaw
    length?: number
    shouldShowMoreCard?: boolean
}

const props = defineProps({
    id: {
        type: String,
        required: true
    },
    collection: {
        type: String as PropType<'jobs' | 'mentors' | 'projects' | 'volunteers' | 'skills'>,
        required: true
    },
    lang: {
        type: String,
        default: ''
    },
    maxItemsToShow: {
        type: Number,
        default: 8
    },
    moreItemsCardData: {
        type: Object as PropType<Partial<ItemsCardType>>,
        default: undefined
    },
    noSeparator: {
        type: Boolean
    },
    loading: {
        type: Boolean
    },
    eager: {
        type: Boolean
    },
    compact: {
        type: Boolean
    }
})

const emit = defineEmits(['onShowMoreButtonClick', 'itemClick', 'loaded'])

const hitsCount = ref(0)
const itemsCount = ref(0)
const componentKey = ref(0)
const relatedData = ref({} as RelatedItems)
const loadLazyComponent = ref(false)
const loadingRelatedItems = ref(true)

const moreItemsCard = computed((): ItemsCardType | undefined => {
    return props.moreItemsCardData
        ? ({
              ...props.moreItemsCardData,
              shouldShowMoreCard: hitsCount.value > props.maxItemsToShow,
              length: hitsCount.value - props.maxItemsToShow
          } as ItemsCardType)
        : undefined
})

const itemsToShowCount = computed((): number => {
    return props.maxItemsToShow
})

const relatedItems = computed((): RelatedItems => {
    return relatedData.value
})

const entityType = computed((): EntityType => {
    const collection = props.collection
    switch (collection) {
        case 'jobs':
            return EntityType.Job
        case 'mentors':
            return EntityType.Mentor
        case 'volunteers':
            return EntityType.Volunteer
        case 'projects':
            return EntityType.Project
        case 'skills':
            return EntityType.Skill
        default:
            console.warn('Unknown collection:', collection)
            return EntityType.Job
    }
})

watch(
    () => loadLazyComponent.value,
    (newValue) => {
        if (newValue) loadRelatedItems()
    }
)

const lazyComponentContainer = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | undefined
let observerTimer: ReturnType<typeof setTimeout> | undefined
onBeforeUnmount(() => {
    observer?.disconnect()
    clearTimeout(observerTimer)
})

onMounted(() => {
    if (props.eager || typeof IntersectionObserver === 'undefined') {
        loadLazyComponent.value = true
        return
    }
    observerTimer = setTimeout(() => {
        if (process.client) {
            observer = new IntersectionObserver(
                (entries, observer) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Load and render the component when the container is visible
                            loadLazyComponent.value = true
                            observer.disconnect() // Stop observing once the component is loaded
                        }
                    })
                },
                {
                    root: null, // Observe the viewport
                    threshold: observerThreshold()
                }
            )
            // Start observing the container
            const container = lazyComponentContainer.value
            if (container) {
                observer.observe(container)
            }
        }
    }, 1000)
})

function onButtonClick() {
    emit('onShowMoreButtonClick')
}

async function loadRelatedItems() {
    try {
        const filterBy = props.collection === 'mentors' ? topExclusionFilter.value || undefined : undefined
        const response = await getRelatedItems(props.collection, props.id, {
            maxItems: props.maxItemsToShow,
            filterBy
        })

        hitsCount.value = response.nbHits
        relatedData.value = {
            items: response.hits || [],
            cardType: entityType.value
        }
        loadingRelatedItems.value = false
        emit('loaded', response.hits?.length || 0)
    } catch (error) {
        console.error('Error loading related items:', error)
        relatedData.value = { items: [], cardType: entityType.value }
        loadingRelatedItems.value = false
        emit('loaded', 0)
    }
}

function observerThreshold(): number {
    if (process.client) {
        if (window.innerWidth < 768) return 0
        else if (window.innerWidth > 768 && window.innerWidth < 1000) return 0.1
        else return 0.3
    }
    return 0.3
}
</script>
